// Widget view-key grammar — the single source of truth for parsing the opaque
// `view` strings the widget table is keyed by, and for the ownership/access
// decision derived from them. Imported by BOTH enforcement points (the core
// API's WidgetViewAccessService and the client's useWidgetViewAccess) so the
// server guard and the client's edit affordances can never drift — the same
// principle as the notes `viewerCanEdit` helper.
//
// The keys are PERSISTED VALUES — never rename them:
//   widget-view-member:<userId>        /my-work            private to that user
//   widget-view-team:<id>              team Dashboard tab  shared
//   widget-view-team:<id>-overview     team Overview tab   shared
//   widget-view-workflow:<id>          workflow Dashboard  shared
//
// Only `team` keys have a variant form (`-overview` — the only variant
// today). Entity ids are cuid-style (no hyphens), so a team key splits at the
// FIRST hyphen after the colon. `member`/`workflow` tails are entity ids
// verbatim — never variant-split, so a hyphenated id (test fixtures) can't be
// misread as `<id>-<variant>` and silently shift ownership.
//
// Pure and synchronous by design: the async inputs (role verdict, lead
// membership, privacy bypass) are resolved by each caller from its own data
// and passed in as the `actor`.

const VIEW_PREFIX = "widget-view-";

export type WidgetViewKind = "member" | "team" | "workflow";

export interface ParsedWidgetView {
  kind: WidgetViewKind;
  /** User id for `member`, team/workflow id otherwise. */
  entityId: string;
  /** Suffix after the entity id (`overview`) — team keys only. */
  variant: string | null;
}

export interface WidgetViewScope {
  kind: WidgetViewKind;
  entityId: string;
  variant: string | null;
}

export interface WidgetViewOwnership {
  visibility: "private" | "shared";
  /** Owning user id — private views only. */
  ownerId: string | null;
  /** Shared views only; null for private views and unparseable keys. */
  scope: WidgetViewScope | null;
}

export interface WidgetViewActor {
  /** Workspace user id (=== client `currentUser.userId`). */
  userId: string;
  /** Resolved `widgets.manage` verdict. */
  canManageWidgets: boolean;
  /** Sync predicate, caller-resolved. */
  leadsTeam: (teamId: string) => boolean;
  /**
   * Owner role, or Admin with the workspace `adminOverrideEnabled` toggle
   * on. The client always passes false.
   */
  privacyBypass: boolean;
}

export interface WidgetViewAccess {
  canRead: boolean;
  canWrite: boolean;
  ownership: WidgetViewOwnership;
}

/**
 * Parse a widget view key. Returns null for anything that isn't one of the
 * known-kind keys — callers must treat null as "unknown key", not an error.
 */
export function parseWidgetView(view: string): ParsedWidgetView | null {
  if (!view.startsWith(VIEW_PREFIX)) return null;
  const rest = view.slice(VIEW_PREFIX.length);
  const colon = rest.indexOf(":");
  if (colon === -1) return null;
  const kind = rest.slice(0, colon);
  if (kind !== "member" && kind !== "team" && kind !== "workflow") return null;
  const tail = rest.slice(colon + 1);
  if (!tail) return null;
  // Only team keys carry a variant; ids are cuid-style (no hyphens) so the
  // first hyphen starts it. Member/workflow tails are the entity id verbatim.
  const hyphen = kind === "team" ? tail.indexOf("-") : -1;
  const entityId = hyphen === -1 ? tail : tail.slice(0, hyphen);
  const variant = hyphen === -1 ? null : tail.slice(hyphen + 1);
  if (!entityId || variant === "") return null;
  return { kind, entityId, variant };
}

/**
 * Derive ownership from a view key. Unparseable keys are treated as shared
 * with no scope — reads stay open and writes require `widgets.manage`, which
 * preserves today's behavior for any future/unknown key.
 */
export function ownershipForView(view: string): WidgetViewOwnership {
  const parsed = parseWidgetView(view);
  if (!parsed) return { visibility: "shared", ownerId: null, scope: null };
  if (parsed.kind === "member") {
    return { visibility: "private", ownerId: parsed.entityId, scope: null };
  }
  return {
    visibility: "shared",
    ownerId: null,
    scope: { kind: parsed.kind, entityId: parsed.entityId, variant: parsed.variant },
  };
}

/**
 * The access decision — one place, two enforcers.
 *
 * Private views: owner-only read AND write (no permission needed by the
 * owner), with the platform privacy-bypass as the only exception.
 * Shared views: read is open to everyone; write requires `widgets.manage`
 * OR — team scope only — being that team's lead. Workflow views get no lead
 * grant (workflows have no lead).
 */
export function widgetViewAccess(view: string, actor: WidgetViewActor): WidgetViewAccess {
  const ownership = ownershipForView(view);
  if (ownership.visibility === "private") {
    const allowed = actor.userId === ownership.ownerId || actor.privacyBypass;
    return { canRead: allowed, canWrite: allowed, ownership };
  }
  const canWrite =
    actor.canManageWidgets ||
    (ownership.scope?.kind === "team" && actor.leadsTeam(ownership.scope.entityId));
  return { canRead: true, canWrite, ownership };
}
